import React from "react";
import { IconButton, Button, Paper, Typography, Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useThemeMode } from "../../contexts/ThemeModeContext";

interface DynamicArraySectionProps<T> {
  title: string;
  items: T[];
  setItems: (items: T[]) => void;
  itemTitle: (index: number) => string;
  createItem: () => T;
  renderItem: (
    item: T,
    index: number,
    updateItem: (updatedItem: T) => void,
  ) => React.ReactNode;
}

export const DynamicArraySection = <T,>({
  title,
  items,
  setItems,
  itemTitle,
  createItem,
  renderItem,
}: DynamicArraySectionProps<T>) => {
  const addItem = () => {
    setItems([...items, createItem()]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, updatedItem: T) => {
    setItems(items.map((item, i) => (i === index ? updatedItem : item)));
  };
  const { mode } = useThemeMode();

  return (
    <div
      className="space-y-6 "
      style={{ overflowY: "auto", maxHeight: "540px" }}
    >
      <Typography variant="h5" className="!mb-4" sx={{ mt: 3, mb: 3 }}>
        {title} ({items.length})
      </Typography>

      {items.map((item, index) => (
        <Paper
          key={(item as any).id ?? index}
          elevation={1}
          sx={{
            p: 3,
            mb: 3,
            border:
              mode === "dark" ? "1px solid #374151" : " 1px solid #e5e7eb", // adjust colors if needed
          }}
        >
          <Box className="flex justify-between items-start mb-2">
            <Typography variant="h6" sx={{ fontSize: "1.1rem" }}>
              {itemTitle(index + 1)}
            </Typography>

            {items.length > 1 && (
              <IconButton
                color="error"
                size="small"
                onClick={() => removeItem(index)}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>

          {renderItem(item, index, (updated) => updateItem(index, updated))}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addItem}
            className="w-full"
            sx={{ mt: 2, mb: 2 }}
          >
            Add New Project
          </Button>
        </Paper>
      ))}
    </div>
  );
};
